/**
 * **REFUND** — mengembalikan uang atas bill yang sudah LUNAS.
 *
 * ### ⛔ Kenapa layar ini akhirnya ada
 * Izin `order:refund` berdiri sejak hari pertama, dan pesan galat sistem ini sendiri menyuruh
 * *"koreksinya lewat refund"* di enam tempat — sementara pintunya tak pernah dibangun. Kasir yang
 * mencarinya tak menemukan, lalu memakai jalan lain: batalkan + ketik ulang. Dua dokumen palsu
 * untuk satu peristiwa nyata.
 *
 * ### ⭐ Per BARIS, bukan per bill
 * Kasus paling sering di lantai bukan "batalkan semuanya" melainkan *"ayamnya gosong, yang lain
 * oke"*. Refund seluruh-bill memaksa kasir menagih ulang yang tak bermasalah — dan pada bill besar
 * itu berarti mengetik belasan baris di depan tamu yang sedang kecewa.
 *
 * ### ⛔⛔ Angkanya TIDAK dihitung di sini
 * Layar cuma menyebut baris & kuantitas. Gross, potongan, service charge, dan pajaknya
 * dialokasikan SERVER dengan aturan sisa-terbesar yang sama dengan mesin pajak. Layar yang ikut
 * menghitung akan menghasilkan angka kedua yang berbeda beberapa rupiah — dan selisih beberapa
 * rupiah adalah yang paling sulit dijelaskan saat tutup buku.
 *
 * ⚠️ Perkiraan yang ditampilkan sebelum menekan sengaja diberi label **perkiraan**: ia dijumlahkan
 * dari nilai baris, sedangkan yang mengikat adalah jawaban server.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { RefundableOrder } from '../api/client';
import { formatRupiah } from '../format';

/**
 * ⛔ Enum kontrak, bukan teks bebas. Keempatnya sebab NYATA di lantai; sisanya `other` yang WAJIB
 * berketerangan. Bukti kenapa: alasan teks bebas di VTEC berakhir 18% kosong.
 */
const REASONS = ['kitchen_error', 'wrong_item', 'customer_complaint', 'wrong_qty', 'other'] as const;

export interface RefundDialogProps {
  readonly orderId: string;
  readonly shiftId: string | null;
  readonly onClose: () => void;
  /** Dipanggil sesudah refund terbit — pemanggil menyegarkan tampilannya. */
  readonly onDone: (grandTotal: string) => void;
}

export function RefundDialog(p: RefundDialogProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<RefundableOrder | null>(null);
  /** Kuantitas yang dipilih per baris, kunci = `orderLineId`. */
  const [pilih, setPilih] = useState<Record<string, number>>({});
  const [reason, setReason] = useState<(typeof REASONS)[number]>('kitchen_error');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void api.refundable(p.orderId)
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      });
    return () => { alive = false; };
  }, [p.orderId]);

  const ubah = (id: string, next: number, max: number) => {
    setPilih((v) => ({ ...v, [id]: Math.max(0, Math.min(next, max)) }));
  };

  /*
   * ⚠️ **Perkiraan**, bukan angka yang mengikat. Ia dijumlahkan dari nilai baris apa adanya, dan
   * pada refund SEBAGIAN alokasi server bisa berbeda beberapa rupiah karena aturan sisa-terbesar.
   * Yang ditampilkan sesudah berhasil adalah angka SERVER.
   */
  const perkiraan = (data?.lines ?? []).reduce((n, l) => {
    const q = pilih[l.orderLineId] ?? 0;
    if (q <= 0) return n;
    const porsi = q / Number(l.qtySold);
    return n + porsi * (Number(l.lineTotal) + Number(l.serviceCharge) + Number(l.taxAmount));
  }, 0);

  const adaPilihan = Object.values(pilih).some((q) => q > 0);
  /* ⛔ `other` tanpa penjelasan = teks bebas jilid dua. Layar menolak dulu; server tetap jaring. */
  const perluCatatan = reason === 'other' && note.trim() === '';

  const kirim = useCallback(async () => {
    if (data === null) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.refund(p.orderId, {
        reasonCode: reason,
        ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
        lines: data.lines
          .filter((l) => (pilih[l.orderLineId] ?? 0) > 0)
          .map((l) => ({
            orderLineId: l.orderLineId,
            qty: `${String(pilih[l.orderLineId] ?? 0)}.000000`,
          })),
        ...(p.shiftId === null ? {} : { shiftId: p.shiftId }),
      });
      p.onDone(r.grandTotal);
    } catch (e) {
      /* ⛔ Penolakan server ditampilkan APA ADANYA: kodenya sudah membawa sebab & jalan keluarnya
         (sesi kas belum dibuka, kuantitas melebihi sisa, hari sudah ditutup). Menerjemahkannya
         ulang di sini melahirkan dua versi kebenaran yang bisa berselisih. */
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [data, note, p, pilih, reason]);

  return (
    <div className="dialog refund-dialog" role="dialog" data-testid="refund-dialog">
      <h2>{t('cashier.refund.title')}</h2>

      {error !== null && <p className="error" data-testid="refund-error">{error}</p>}

      {data === null ? (
        <p className="empty">{t('common.loading')}</p>
      ) : (
        <>
          <ul className="refund-lines">
            {data.lines.map((l) => {
              const max = Math.floor(Number(l.qtyRefundable));
              const q = pilih[l.orderLineId] ?? 0;
              return (
                <li key={l.orderLineId} data-testid={`refund-line-${l.productCode}`}>
                  <span className="refund-name">{l.productName}</span>
                  {/*
                    ⭐ Sisa DIKATAKAN, bukan ditebak kasir. Baris yang sudah habis dikembalikan
                    tetap TERLIHAT dengan angka nol — yang lenyap membuat kasir mengira ia salah
                    membuka bill.
                  */}
                  <span className="refund-sisa" data-testid={`refund-sisa-${l.productCode}`}>
                    {t('cashier.refund.remaining', { n: max })}
                  </span>
                  <span className="refund-stepper">
                    <button
                      type="button"
                      data-testid={`refund-minus-${l.productCode}`}
                      disabled={q <= 0}
                      onClick={() => { ubah(l.orderLineId, q - 1, max); }}
                    >−</button>
                    <b data-testid={`refund-qty-${l.productCode}`}>{q}</b>
                    <button
                      type="button"
                      data-testid={`refund-plus-${l.productCode}`}
                      disabled={q >= max}
                      onClick={() => { ubah(l.orderLineId, q + 1, max); }}
                    >+</button>
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="refund-reason">
            <select
              data-testid="refund-reason"
              value={reason}
              onChange={(e) => { setReason(e.target.value as (typeof REASONS)[number]); }}
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>{t(`reasonCode.${r}`)}</option>
              ))}
            </select>
            <input
              data-testid="refund-note"
              maxLength={200}
              placeholder={t('cashier.refund.note')}
              value={note}
              onChange={(e) => { setNote(e.target.value); }}
            />
          </div>

          <p className="refund-total" data-testid="refund-perkiraan">
            {t('cashier.refund.estimate')} <b>{formatRupiah(perkiraan.toFixed(4))}</b>
          </p>
        </>
      )}

      <div className="dialog-actions">
        <button type="button" data-testid="refund-cancel" onClick={p.onClose}>
          {t('cashier.refund.cancel')}
        </button>
        <button
          type="button"
          className="action-primary"
          data-testid="refund-submit"
          disabled={busy || !adaPilihan || perluCatatan}
          onClick={() => void kirim()}
        >
          {busy ? t('common.loading') : t('cashier.refund.submit')}
        </button>
      </div>
    </div>
  );
}
